import React from 'react';
import { motion } from 'framer-motion';
import { Check, Star, Lock, ShieldCheck, BadgeCheck, ArrowRight } from 'lucide-react';
import { Button } from './ui/Button';
import { PaymentMethods } from './PaymentMethods';
interface LineItem {
  name: string;
  price: string;
}
interface OfferCardProps {
  /** Items shown in the value stack */
  items?: LineItem[];
  /** Whether to render the detailed value-stack (main offer) or a compact version (final CTA) */
  variant?: 'full' | 'compact';
}
const defaultItems: LineItem[] = [{
  name: 'Volume 1: Full Body Sessions',
  price: '$27'
}, {
  name: 'Volume 2: Targeted Zone Sessions',
  price: '$27'
}, {
  name: 'Volume 3: Morning & Evening Protocols',
  price: '$27'
}, {
  name: 'Volume 4: Special Populations',
  price: '$27'
}, {
  name: 'Volume 5: Express Formats',
  price: '$27'
}, {
  name: 'Volume 6: Themed Classes',
  price: '$27'
}, {
  name: '5 Exclusive Free Bonuses — 400+ Pages',
  price: '$82'
}, {
  name: 'Instant PDF Access',
  price: '$18'
}];
const trustBadges = [{
  icon: Lock,
  label: 'Secure Payment'
}, {
  icon: ShieldCheck,
  label: '7-Day Guarantee'
}, {
  icon: BadgeCheck,
  label: 'Verified Purchase'
}];
export function OfferCard({
  items = defaultItems,
  variant = 'full'
}: OfferCardProps) {
  return <div className="relative rounded-2xl sm:rounded-3xl p-[2px] overflow-hidden shadow-[0_0_40px_-10px_rgba(45,212,191,0.3)] hover:shadow-[0_0_60px_-15px_rgba(45,212,191,0.5)] transition-shadow duration-500" data-id="element-105">
      {/* Animated gradient border */}
      <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(45,212,191,0.5)_50%,transparent_75%,transparent_100%)] bg-[length:250%_250%,100%_100%] animate-[shimmer_3s_linear_infinite]" data-id="element-106" />

      <div className="relative h-full w-full overflow-hidden rounded-[calc(1rem-2px)] sm:rounded-[calc(1.5rem-2px)] bg-brand-950 ring-1 ring-brand-500/30" data-id="element-107">
        {/* Ambient glow */}
        <div className="pointer-events-none absolute -top-1/3 left-1/2 -translate-x-1/2 h-[28rem] w-[28rem] rounded-full bg-brand-500/20 blur-[100px]" data-id="element-108" />

        <div className="relative px-5 py-8 sm:px-10 sm:py-12" data-id="element-109">
          {/* Complete offer pill */}
          <div className="flex justify-center" data-id="element-110">
            <motion.span animate={{
            scale: [1, 1.05, 1]
          }} transition={{
            repeat: Infinity,
            duration: 2,
            ease: 'easeInOut'
          }} className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-300 to-amber-400 px-4 sm:px-5 py-2 text-xs sm:text-sm font-bold uppercase tracking-[0.15em] text-brand-950 shadow-lg shadow-amber-400/40" data-id="element-111">
              <Star className="h-4 w-4 fill-current" data-id="element-112" />
              The Complete Offer
            </motion.span>
          </div>

          {/* Heading */}
          <h3 className="mt-6 sm:mt-8 text-center font-display text-2xl sm:text-4xl font-extrabold uppercase tracking-tight text-white" data-id="element-113">
            Unlock Your <span className="text-brand-400" data-id="element-114">Stretching Ready</span>{' '}
            Today:
          </h3>

          {/* Value stack */}
          {variant === 'full' && <div className="mx-auto mt-8 sm:mt-10 max-w-xl" data-id="element-115">
              <p className="mb-4 text-center text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-brand-300/70" data-id="element-116">
                Total Content Value
              </p>
              <ul className="space-y-3 sm:space-y-4" data-id="element-117">
                {items.map(item => <li key={item.name} className="flex items-center justify-between gap-3" data-id="element-118">
                    <span className="flex items-center gap-3 text-sm sm:text-base text-slate-100" data-id="element-119">
                      <span className="flex h-6 w-6 sm:h-7 sm:w-7 flex-none items-center justify-center rounded-full bg-brand-500/20 ring-1 ring-brand-400/40" data-id="element-120">
                        <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-brand-300" data-id="element-121" />
                      </span>
                      <span className="leading-tight" data-id="element-122">{item.name}</span>
                    </span>
                    <span className="flex-none text-sm sm:text-base font-medium text-rose-400/80 line-through" data-id="element-123">
                      {item.price}
                    </span>
                  </li>)}
              </ul>

              <div className="mt-5 sm:mt-6 flex items-center justify-between border-t border-white/10 pt-5 sm:pt-6" data-id="element-124">
                <span className="font-display text-base sm:text-lg font-extrabold uppercase tracking-wide text-white" data-id="element-125">
                  Real Value
                </span>
                <span className="text-base sm:text-lg font-bold text-rose-400 line-through" data-id="element-126">
                  $235
                </span>
              </div>
            </div>}

          {/* Price */}
          <div className="mt-8 sm:mt-10 flex flex-col items-center" data-id="element-127">
            <div className="flex items-center gap-3" data-id="element-128">
              <span className="text-xl sm:text-2xl font-semibold text-slate-400 line-through" data-id="element-129">
                $235
              </span>
              <span className="rounded-md bg-brand-500/15 px-2.5 py-1 text-xs sm:text-sm font-bold text-brand-300 ring-1 ring-brand-400/40" data-id="element-130">
                86% OFF
              </span>
            </div>
            <motion.span animate={{
            scale: [1, 1.02, 1]
          }} transition={{
            repeat: Infinity,
            duration: 2,
            ease: 'easeInOut'
          }} className="mt-2 font-display text-6xl sm:text-7xl font-extrabold tracking-tight text-white drop-shadow-[0_0_15px_rgba(45,212,191,0.5)]" data-id="element-131">
              $27
            </motion.span>
            <p className="mt-3 text-xs sm:text-sm text-slate-400" data-id="element-132">
              One-time payment · Instant access
            </p>
          </div>

          {/* CTA */}
          <div className="mx-auto mt-7 sm:mt-9 max-w-xl" data-id="element-133">
            <Button size="xl" fullWidth onClick={() => {
            window.location.href = 'https://stretching-ready.impultienda.ar/checkout';
          }} className="group relative overflow-hidden !rounded-xl bg-gradient-to-r from-brand-400 to-brand-300 py-4 sm:py-5 text-base sm:text-lg font-bold uppercase tracking-wide text-brand-950 hover:from-brand-300 hover:to-brand-200 active:scale-[0.98] shadow-[0_0_20px_rgba(45,212,191,0.4)] hover:shadow-[0_0_30px_rgba(45,212,191,0.6)] transition-all" data-id="element-134">
              <span className="relative z-10 flex items-center justify-center" data-id="element-135">
                Get Stretching Ready Now
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" data-id="element-136" />
              </span>
              {/* Shine effect */}
              <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent group-hover:animate-[shimmer_1.5s_infinite]" data-id="element-137" />
            </Button>
            <p className="mt-4 text-center text-xs sm:text-sm italic text-slate-400" data-id="element-138">
              Everything you need to teach with confidence — starting today.
            </p>
          </div>

          {/* Trust badges */}
          <div className="mx-auto mt-7 sm:mt-9 grid max-w-xl grid-cols-3 gap-2 sm:gap-3" data-id="element-139">
            {trustBadges.map(({
            icon: Icon,
            label
          }) => <div key={label} className="flex flex-col items-center gap-1.5 sm:gap-2 rounded-xl bg-white/5 px-2 py-3 sm:px-3 sm:py-4 text-center ring-1 ring-white/10" data-id="element-140">
                <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-brand-400" data-id="element-141" />
                <span className="text-[11px] sm:text-sm font-semibold leading-tight text-white" data-id="element-142">
                  {label}
                </span>
              </div>)}
          </div>

          {/* Payment methods */}
          <div className="mt-7 sm:mt-9 text-center" data-id="element-143">
            <p className="mb-3 text-[10px] sm:text-xs font-semibold uppercase tracking-[0.2em] text-slate-500" data-id="element-144">
              Accepted Payments
            </p>
            <PaymentMethods data-id="element-145" />
          </div>
        </div>
      </div>
    </div>;
}